/* ==========================================================================
   Landing page — scroll reveal
   Ties `.lp-reveal` elements to the scroll position: they rise into place
   while scrolling down and sink back while scrolling up.

   Nothing fades: the element drifts, so it travels slower than the page and
   settles into place — visible the whole time, never a curtain.

   Progress is driven by HOW MUCH of the element is on screen, not by where
   its top edge is — so a tall image comes in piece by piece as you scroll
   instead of finishing while only a sliver is visible. It has fully settled
   once COVERAGE of it is in view (of the element, or of the viewport for
   elements taller than the screen).

   Self-contained so the lp-* sections do not depend on the theme's global
   animation setting.
   ========================================================================== */

(function () {
  if (window.lpReveal) return;

  const SELECTOR = '.lp-reveal';
  const READY_CLASS = 'lp-reveal-ready';
  const REVEALED_CLASS = 'is-revealed';
  const TRACKED_ATTRIBUTE = 'data-lp-reveal-tracked';
  const DEFAULT_SHIFT = '12rem';
  // Share of the element (or of the viewport) that has to be visible before
  // the reveal is complete.
  const COVERAGE = 0.8;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const tracked = [];
  let frameRequested = false;

  function collect(root) {
    return Array.from((root || document).querySelectorAll(SELECTOR)).filter(
      (element) => !element.hasAttribute(TRACKED_ATTRIBUTE)
    );
  }

  // Resolves a CSS length custom property (rem / em / vh / px) to pixels.
  function toPixels(element, property, fallback) {
    const raw = getComputedStyle(element).getPropertyValue(property).trim() || fallback;
    const value = parseFloat(raw);
    if (!value) return 0;
    if (raw.endsWith('rem')) return value * parseFloat(getComputedStyle(document.documentElement).fontSize);
    if (raw.endsWith('em')) return value * parseFloat(getComputedStyle(element).fontSize);
    if (raw.endsWith('vh')) return (value * window.innerHeight) / 100;
    return value;
  }

  function progressFor(element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Height of the element visible from the bottom edge of the viewport
    // upwards. Derived from the top edge only, so it keeps growing and never
    // falls back while the element scrolls out past the top.
    const entered = Math.min(viewportHeight - rect.top, rect.height);
    const span = Math.min(rect.height, viewportHeight) * COVERAGE;
    if (span <= 0) return 1;

    const progress = entered / span;
    return progress < 0 ? 0 : progress > 1 ? 1 : progress;
  }

  function apply(entry, progress) {
    if (entry.progress === progress) return;
    entry.progress = progress;

    // Smoothstep, not ease-out: it holds the offset through the middle of the
    // travel instead of spending it all up front, so the element keeps lagging
    // behind the page and then eases off at both ends. An ease-out here is
    // already back in place by the time a third of the element is on screen.
    const eased = progress * progress * (3 - 2 * progress);
    entry.element.style.transform =
      eased >= 1 ? 'none' : 'translate3d(0, ' + ((1 - eased) * entry.shift).toFixed(2) + 'px, 0)';
  }

  function paint() {
    frameRequested = false;
    tracked.forEach((entry) => apply(entry, progressFor(entry.element)));
  }

  function requestPaint() {
    if (frameRequested) return;
    frameRequested = true;
    requestAnimationFrame(paint);
  }

  function init(root) {
    const elements = collect(root);
    if (elements.length === 0) return;

    if (prefersReducedMotion) {
      elements.forEach((element) => element.classList.add(REVEALED_CLASS));
      return;
    }

    elements.forEach((element) => {
      element.setAttribute(TRACKED_ATTRIBUTE, '');
      tracked.push({
        element: element,
        shift: toPixels(element, '--lp-reveal-shift', DEFAULT_SHIFT),
        progress: null,
      });
    });

    paint();
  }

  if (!prefersReducedMotion) {
    document.documentElement.classList.add(READY_CLASS);
    window.addEventListener('scroll', requestPaint, { passive: true });
    window.addEventListener('resize', () => {
      tracked.forEach((entry) => {
        entry.shift = toPixels(entry.element, '--lp-reveal-shift', DEFAULT_SHIFT);
      });
      requestPaint();
    });
    // Images settle late and shift everything below them.
    window.addEventListener('load', requestPaint);
  }

  window.lpReveal = { init: init, refresh: requestPaint };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('shopify:section:reorder', () => init());
  }
})();
